// Angular
import { ErrorHandler, NgModule } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
// Translate
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createTranslateLoader } from './translate.loader';
// NGXS
import { NgxsModule, NoopNgxsExecutionStrategy } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsResetPluginModule } from 'ngxs-reset-plugin';
// import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
// Prime Ng
import { MessageService } from 'primeng/api';
// Sentry
import * as Sentry from "@sentry/angular";
// Medsurf Lib
import { FLAT_SETTINGS } from '@medsurf/flat-services';
import { NgxsHistoryPluginModule } from '@medsurf/flat-plugins';
import * as FlatStore from '@medsurf/flat-states';
import * as FlatFacades from '@medsurf/flat-facades';
// Env
import { environment } from '../environments/environment';
import { DEFAULT_VALUES } from './common/constants/default-values';
import defaultValues from './common/constants/models/defaultvalues.json';
// Modules
import { MarkdownModule } from 'ngx-markdown';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AdminModule } from './admin/admin.module';
import { AppRoutingModule } from './app-routing.module';
import { ChapterSlideModule } from "./chapter-slide/chapter-slide.module";
import { ErrorsModule } from './errors/errors.module';
import { MedsurfCommonModule } from './common/medsurf-common.module';
import { SearchModule } from './search/search.module';
import { UserModule } from './user/user.module';
import { FlatDesignModule } from '@medsurf/flat-designs';
// App
import { AppComponent } from './app.component';
// Components
import { ChangelogComponent } from './changelog/changelog.component';

@NgModule({
  imports: [
    // Angular
    BrowserModule,
    HttpClientModule,
    // Translate
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    // NGXS
    NgxsModule.forRoot([
      // Flat Control Store
      FlatStore.AlertControlState,
      FlatStore.AuthControlState,
      FlatStore.GeneralControlState,
      FlatStore.HistoryControlState,
      FlatStore.MetadataControlState,
      FlatStore.NavigationControlState,
      FlatStore.NodeControlState,
      FlatStore.PageControlState,
      FlatStore.SearchControlState,
      FlatStore.SlideControlState,
      // Flat Entity Store
      FlatStore.AnnotationEntityState,
      FlatStore.AnnotationGroupEntityState,
      FlatStore.AnnotationLabelEntityState,
      FlatStore.AnnotationSourceEntityState,
      FlatStore.AnnotationTargetEntityState,
      FlatStore.ChoiceEntityState,
      FlatStore.CopyrightEntityState,
      FlatStore.FreeFormStyleEntityState,
      FlatStore.ImageEntityState,
      FlatStore.KeymapColumnEntityState,
      FlatStore.KeymapLabelEntityState,
      FlatStore.KeymapStyleEntityState,
      FlatStore.MediaEntityState,
      FlatStore.MetadataEntityState,
      FlatStore.ModalityEntityState,
      FlatStore.NodeEntityState,
      FlatStore.PageEntityState,
      FlatStore.PointOfInterestEntityState,
      FlatStore.QuestionEntityState,
      FlatStore.SlideLayerEntityState,
      FlatStore.StainEntityState,
      FlatStore.UserEntityState,
      FlatStore.VocabularyGroupEntityState,
      FlatStore.VocabularyWordEntityState,
      // Flat Shared Store
      FlatStore.ImageSharedState,
      FlatStore.MenuSharedState,
      FlatStore.TrainingSharedState,
      FlatStore.UserSharedState,
      // Flat Author Store
      FlatStore.BookmarkAuthorState,
      FlatStore.BulkEditAuthorState,
      FlatStore.ConstantAuthorState,
      FlatStore.MediaAuthorState,
      FlatStore.ReleaseNoteAuthorState,
      FlatStore.SlideAuthorState,
      FlatStore.TextExportAuthorState,
    ], {
      executionStrategy: NoopNgxsExecutionStrategy,
      /* DEVELOP * /
      developmentMode: true,
      selectorOptions: {
        suppressErrors: false,
        injectContainerState: false,
      }
      /**/
      /* PROD */
      developmentMode: false,
      selectorOptions: {
        suppressErrors: true,
        injectContainerState: false,
      }
      /**/
    }),
    NgxsStoragePluginModule.forRoot({
      key: [
        'authControl.token',
        'authControl.tokenLifetime',
        'authControl.expiresIn',
        'generalControl.useLegacyEditor',
        'bookmarkAuthor.bookmarks',
        'slideAuthor.theme',
        'slideAuthor.layout',
        'slideAuthor.chapterPanelData',
        'slideAuthor.layerPanelData',
        'slideAuthor.wysiwygPanelData',
        'slideAuthor.editPanelOverlay',
        'slideAuthor.editPanelData',
        'menuShared.showVirtualPointer',
        'menuShared.showNavigator',
        'menuShared.showMarkers',
        'menuShared.showSelftest',
        'menuShared.showCaseHistory',
      ],
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxsHistoryPluginModule.forRoot({}),
    NgxsResetPluginModule.forRoot(),
    /* TODO IMPORTANT Uncomment for production * /
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
      actionSanitizer: (action) => action.dirtyNode ? {...action, dirtyNode: '<<LONG_BLOB>>'} : action,
      stateSanitizer: (state) => state.dirtyNodes ? {...state, dirtyNodes: '<<LONG_BLOB>>'} : state
    }),
    /**/
    MarkdownModule.forRoot(),
    NgbModule,
    // Modules
    AdminModule,
    AppRoutingModule,
    ChapterSlideModule,
    ErrorsModule,
    MedsurfCommonModule,
    SearchModule,
    UserModule,
    FlatDesignModule,
  ],
  declarations: [
    AppComponent,
    ChangelogComponent
  ],
  providers: [
    // Error Handlers
    // TODO enable dialog
    {provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false })},
    // {provide: Sentry.TraceService, deps: [Router]},
    // {provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true},
    // Prime Ng
    {provide: MessageService, useClass: MessageService},
    // Settings
    {provide: FLAT_SETTINGS, useValue: environment.config},
    {provide: DEFAULT_VALUES, useValue: (<any>defaultValues).fields},
    // Control Facades
    {provide: FlatFacades.AlertControlFacade, useClass: FlatFacades.AlertControlFacade},
    {provide: FlatFacades.AuthControlFacade, useClass: FlatFacades.AuthControlFacade},
    {provide: FlatFacades.NavigationControlFacade, useClass: FlatFacades.NavigationControlFacade},
    {provide: FlatFacades.SearchControlFacade, useClass: FlatFacades.SearchControlFacade},
    // Guard Facades
    {provide: FlatFacades.BaseGuardChainFacade, useClass: FlatFacades.BaseGuardChainFacade},
    // Shared Facades
    {provide: FlatFacades.AppSharedFacade, useClass: FlatFacades.AppSharedFacade},
    {provide: FlatFacades.HistorySharedFacade, useClass: FlatFacades.HistorySharedFacade},
    {provide: FlatFacades.ImageSharedFacade, useClass: FlatFacades.ImageSharedFacade},
    {provide: FlatFacades.MenuSharedFacade, useClass: FlatFacades.MenuSharedFacade},
    {provide: FlatFacades.NodeSharedFacade, useClass: FlatFacades.NodeSharedFacade},
    {provide: FlatFacades.PageSharedFacade, useClass: FlatFacades.PageSharedFacade},
    {provide: FlatFacades.TrainingSharedFacade, useClass: FlatFacades.TrainingSharedFacade},
    {provide: FlatFacades.UserSharedFacade, useClass: FlatFacades.UserSharedFacade},
    {provide: FlatFacades.VocabularySharedFacade, useClass: FlatFacades.VocabularySharedFacade},
    // Author Facades
    {provide: FlatFacades.BookmarkAuthorFacade, useClass: FlatFacades.BookmarkAuthorFacade},
    {provide: FlatFacades.ClipboardAuthorFacade, useClass: FlatFacades.ClipboardAuthorFacade},
    {provide: FlatFacades.BulkEditAuthorFacade, useClass: FlatFacades.BulkEditAuthorFacade},
    {provide: FlatFacades.ConstantAuthorFacade, useClass: FlatFacades.ConstantAuthorFacade},
    {provide: FlatFacades.MediaAuthorFacade, useClass: FlatFacades.MediaAuthorFacade},
    {provide: FlatFacades.ReleaseNoteAuthorFacade, useClass: FlatFacades.ReleaseNoteAuthorFacade},
    {provide: FlatFacades.SlideAuthorFacade, useClass: FlatFacades.SlideAuthorFacade},
    {provide: FlatFacades.TextExportAuthorFacade, useClass: FlatFacades.TextExportAuthorFacade},
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
